/**
 * PSV-1888 — storefront-driven shop content.
 *
 * The platform's RUNTIME endpoints (`GET /storefronts`, `GET
 * /catalog/items` — root-mounted, NOT under /api/v1) accept the game's
 * pk_ client key as the Bearer, so the browser reads the merchandising
 * directly: WHICH cosmetics are on sale and in WHAT ORDER comes from the
 * published storefront's item list; catalog items map ids to skus.
 *
 * The platform carries NO prices/currency (RemoveCatalogPrices) and no
 * purchase flow — the gem economy is the game's own: prices + render
 * payloads live in shared/cosmetics.ts keyed by sku, purchases go
 * through the server-authoritative PurchaseCosmetic RPC.
 *
 * Resilience: any failure (network, no published storefront, empty
 * sections) → null; the caller falls back to the full shared table so
 * the shop never renders empty because of a platform blip.
 */

import { COSMETICS, cosmeticById, type CosmeticDef } from '../../server/shared/cosmetics';

interface StorefrontDto {
  id: string;
  name: string;
  status: string;
  // Wire is snake_case (item_id); tolerate camelCase for future drift.
  items?: Array<{ item_id?: string; itemId?: string; position?: number }>;
}

interface CatalogItemSummaryDto {
  id: string;
  sku?: string;
  status?: string;
}

function platformHttpBase(): string {
  // wss://host/ws → https://host
  const ws = import.meta.env.VITE_PLAYSERV_ENDPOINT ?? '';
  return ws.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://').replace(/\/ws$/, '');
}

async function getJson<T>(path: string): Promise<T | null> {
  const key = import.meta.env.VITE_PLAYSERV_GAME_ACCESS_TOKEN ?? '';
  try {
    // Runtime credential slot: a pk_ key rides the X-Playserv-Client
    // header (Authorization is reserved for sk_/player JWTs — a pk_
    // there is rejected with invalid_credentials).
    const resp = await fetch(`${platformHttpBase()}${path}`, {
      headers: { 'X-Playserv-Client': key },
    });
    if (!resp.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[shop] ${path} → ${resp.status}`);
      return null;
    }
    return (await resp.json()) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[shop] ${path} fetch failed:`, err);
    return null;
  }
}

/**
 * Ordered cosmetic defs to render in the shop. Storefront decides order;
 * unknown skus (catalog items with no shared-table entry) are skipped
 * with a warning. Falls back to the full shared table on ANY gap.
 */
export async function loadShopEntries(): Promise<readonly CosmeticDef[]> {
  const storefronts = await getJson<{ data?: StorefrontDto[] } | StorefrontDto[]>(
    '/storefronts',
  );
  const list = Array.isArray(storefronts) ? storefronts : (storefronts?.data ?? []);
  // publish_storefront transitions draft → LIVE (not 'published' — that's
  // the catalog-item vocabulary); accept both to survive vocabulary drift.
  const live = list.find(
    (s) => (s.status === 'live' || s.status === 'published') && (s.items?.length ?? 0) > 0,
  );
  if (live === undefined) {
    // eslint-disable-next-line no-console
    console.info('[shop] no published storefront — rendering the full shared table');
    return COSMETICS;
  }

  const catalog = await getJson<{ data?: CatalogItemSummaryDto[] } | CatalogItemSummaryDto[]>(
    '/catalog/items?status=published&limit=100',
  );
  const catalogList = Array.isArray(catalog) ? catalog : (catalog?.data ?? []);
  const skuByItemId = new Map(
    catalogList.map((i) => [i.id, i.sku ?? ''] as const),
  );

  const ordered = [...(live.items ?? [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((it) => cosmeticById(skuByItemId.get(it.item_id ?? it.itemId ?? '') ?? ''))
    .filter((def): def is CosmeticDef => def !== null);

  if (ordered.length === 0) {
    // eslint-disable-next-line no-console
    console.warn('[shop] storefront items did not resolve to known skus — falling back');
    return COSMETICS;
  }
  // eslint-disable-next-line no-console
  console.info(`[shop] storefront "${live.name}" drives the shop (${ordered.length} items)`);
  return ordered;
}
